import { useEffect, useState, useCallback, useRef } from 'react';
import Box from '@mui/material/Box';

import ChatSidebar from './ChatSidebar';
import ChatWindow from './ChatWindow';
import APIService from '../../api/apiService';
import chatApi from './chat.api';
import { useUser } from '../../auth/useUser';
import { getDarkThemeClass, landingPageRoute } from '../utility/constants';
import { useMemo } from 'react';
import { useTheme } from '@mui/material';
import { useToken } from '../../utility';
import { failsTokenAndSetupCheck } from '../../checks/setup';
import { Navigate } from 'react-router';

export default function GrowAssistance() {
  const [values, setValues] = useState({
    loading: false,
    farms: [],
    activeFarm: null,
    sessions: [],
    activeSessionId: null,
    isNewSession: false,
  });

  const {
    loading,
    farms,
    activeFarm,
    sessions,
    activeSessionId,
    isNewSession,
  } = values;

  const newChatRef = useRef(false);

  const user = useUser();


  const USER_ID = user?.user_id ? String(user.user_id) : null;

  const deletedSessionIdsRef = useRef(new Set());


  const theme = useTheme();

  const currDarkThemeClass = useMemo(
    () => getDarkThemeClass(theme),
    [theme?.palette?.themeMode]
  );

  const isDark = currDarkThemeClass === 'dark-mode';

  const setVal = (key, value) =>
    setValues(prev => ({ ...prev, [key]: value }));

  const {token , tokenDetails} = useToken()
  const invalidTokenState = useMemo(()=>failsTokenAndSetupCheck(token,tokenDetails),[token])


  /* ---------------- Load Farms ---------------- */
  useEffect(() => {

    if (invalidTokenState)return;

    const loadFarms = async () => {
      setVal('loading', true);
      try {
        const res = await APIService.fetchInstance('collection/farm/');
        const farmsData = res?.data?.farms?.results || [];
        setValues(prev => ({
          ...prev,
          farms: farmsData,
          activeFarm: farmsData[0] || null,
        }));
      } finally {
        setVal('loading', false);
      }
    };
    loadFarms();
  }, [invalidTokenState]);

  /* ---------------- Load Sessions ---------------- */
  const loadSessions = useCallback(async () => {
    if (!activeFarm?.id || !USER_ID) return;

    const res = await chatApi.getChatSessions({
      farm_id: activeFarm.id,
      user_id: USER_ID,
      limit: 50,
    });

    setVal(
      'sessions',
      (res?.data?.sessions || []).filter(
        s => !deletedSessionIdsRef.current.has(s.session_id)
      )
    );
  }, [activeFarm?.id, USER_ID]);

  useEffect(() => {
  loadSessions();
}, [loadSessions]);



  /* ---------------- Handlers ---------------- */

  const handleNewChat = () => {
  handleSessionCreated(null);
};

  const handleFarmChange = farm => {
    if (activeFarm?.id === farm?.id) return;
    setValues(prev => ({
      ...prev,
      activeFarm: farm,
      sessions: [],
      activeSessionId: null,
      isNewSession: false,
    }));
  };

  const handleSessionChange = sessionId => {
    setValues(prev => ({
      ...prev,
      activeSessionId: sessionId,
      isNewSession: false,
    }));
  };

  const handleSessionCreated = sessionId => {
    newChatRef.current = true;

    setValues(prev => ({
      ...prev,
      activeSessionId: sessionId,
      isNewSession: true,
    }));
  };

  const handleSessionDelete = sessionId => {
    deletedSessionIdsRef.current.add(sessionId);

    setValues(prev => ({
      ...prev,
      sessions: prev.sessions.filter(
        s => s.session_id !== sessionId
      ),
      activeSessionId:
        prev.activeSessionId === sessionId ? null : prev.activeSessionId,
      isNewSession: false,
    }));
  };

const handleFirstMessageSent = sessionId => {
  setValues(prev => ({
    ...prev,
    activeSessionId: sessionId,
    isNewSession: false,
  }));
};



  if (loading) return <div>Loading…</div>;
  if (!activeFarm) return <div>No farms</div>;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', width: '100%' }}>
      <ChatSidebar
        farms={farms}
        activeFarm={activeFarm}
        sessions={sessions}
        activeSessionId={activeSessionId}
        userId={USER_ID}
        onFarmChange={handleFarmChange}
        onSessionChange={handleSessionChange}
        onSessionCreated={handleSessionCreated}
        onSessionDelete={handleSessionDelete}
        isDark={isDark}
        currDarkThemeClass={currDarkThemeClass}
        onNewChat={handleNewChat}
      />

      <ChatWindow
        farmId={activeFarm.id}
        sessionId={activeSessionId}
        userId={USER_ID}
        isNewSession={isNewSession}
        onFirstMessageSent={handleFirstMessageSent}
        isDark={isDark}
        currDarkThemeClass={currDarkThemeClass}
        onNewChat={handleNewChat}
        onHistoryLoaded={loadSessions}  
      />
    </Box>
  );
}